import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { api } from '../utils/api';
import {
  equipmentCreateUrl,
  equipmentUrl,
  rackEquipmentQueryKey,
} from './equipmentWrite.logic';
import type { RackEquipmentItem } from '../components/canvas/rackTypes';
import type { CatalogEntry, AISuggestion } from '../utils/equipmentApi';

interface UseEquipmentMutationsParams {
  actualCode: string;
  actualRackId: string | number | undefined;
  workspace: string;
  editingItem: RackEquipmentItem | null;
  setEditingItem: (item: RackEquipmentItem | null) => void;
  setIsFormOpen: (open: boolean) => void;
  setFormInitialData: (data: any) => void;
  setSelectedEq: (eq: RackEquipmentItem | null) => void;
}

export function useEquipmentMutations({
  actualCode,
  actualRackId,
  workspace,
  editingItem,
  setEditingItem,
  setIsFormOpen,
  setFormInitialData,
  setSelectedEq,
}: UseEquipmentMutationsParams) {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const listKey = rackEquipmentQueryKey(actualCode, actualRackId, workspace);

  // ── Drag & drop position update
  const updateEquipmentPos = useMutation({
    mutationFn: (data: { id: string; u_position: number; mounting_position: string }) =>
      api.put(equipmentUrl(data.id, workspace), {
        u_position: data.u_position,
        mounting_position: data.mounting_position,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...listKey] });
      toast.success('Позиция оборудования обновлена');
    },
    onError: (error: any) => {
      const msg = error.response?.data?.detail || error.message || 'Не удалось переместить прибор';
      toast.error(`Ошибка монтажа: ${msg}`);
      queryClient.invalidateQueries({ queryKey: [...listKey] });
    },
  });

  const handleEquipmentMove = (id: string, newU: number, newMount: string) => {
    updateEquipmentPos.mutate({ id, u_position: newU, mounting_position: newMount });
  };

  // ── Form submit (create / update)
  const handleFormSubmit = async (formData: any) => {
    setIsSaving(true);
    try {
      const payload = {
        name:              formData.name,
        equipment_type:    formData.equipment_type,
        u_height:          Number(formData.u_height),
        u_position:        Number(formData.u_position),
        mounting_position: formData.mounting_position || 'Front',
        is_zero_u:         Boolean(formData.is_zero_u),
        weight_kg:         Number(formData.weight_kg) || 0,
        power_w:           Number(formData.power_w)   || 0,
        serial_number:     formData.serial_number?.trim() || null,
        asset_tag:         formData.asset_tag?.trim()     || null,
        device_role:       formData.device_role || 'Server',
        status:            formData.status      || 'Active',
        airflow:           formData.airflow     || 'Front-to-Rear',
        depth:             formData.depth       || 'Full Depth (800mm)',
        attributes:        typeof formData.attributes === 'string'
          ? JSON.parse(formData.attributes || '{}')
          : (formData.attributes || {}),
        rack_id:           Number(actualRackId),
        datacenter_code:   actualCode,
        catalog_id:        formData.catalog_id || null,
      };

      const isEdit = !!editingItem?.id;
      if (isEdit) {
        await api.put(equipmentUrl(editingItem.id, workspace), payload);
        toast.success('Оборудование обновлено');
        setSelectedEq({ ...editingItem, ...payload, id: editingItem.id });
      } else {
        await api.post(equipmentCreateUrl(actualCode, actualRackId!, workspace), payload);
        toast.success('Оборудование добавлено в стойку');
        setSelectedEq(null);
      }

      setIsFormOpen(false);
      setEditingItem(null);
      setFormInitialData(null);
      queryClient.invalidateQueries({ queryKey: [...listKey] });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения оборудования');
    } finally {
      setIsSaving(false);
    }
  };

  // ── AI suggestion with long-poll
  const handleAISuggest = async (
    query: string,
    type: string | undefined,
    targetU: number | undefined,
    onProgress: (phase: string) => void,
    signal?: AbortSignal,
  ): Promise<AISuggestion> => {
    const startRes = await api.post('/api/equipment/suggest', {
      rack_id:           Number(actualRackId),
      query,
      equipment_type:    type || undefined,
      target_u_position: targetU,
    }, { signal });
    const taskId = startRes.data.task_id;

    return new Promise<AISuggestion>((resolve, reject) => {
      let timeoutId: any = null;

      const cleanup = () => { if (timeoutId) clearTimeout(timeoutId); };

      if (signal) {
        signal.addEventListener('abort', () => { cleanup(); reject(new Error('Aborted')); });
      }

      const poll = async () => {
        if (signal?.aborted) return reject(new Error('Aborted'));
        try {
          const statusRes = await api.get(`/api/equipment/suggest/status/${taskId}`, { signal });
          const { status, phase, result, error } = statusRes.data;

          if (status === 'failed')    return reject(new Error(error || 'AI suggestion failed'));
          if (status === 'completed') return resolve(result as AISuggestion);
          if (phase) onProgress(phase);

          if (!signal?.aborted) {
            timeoutId = setTimeout(poll, 2000);
          }
        } catch (err) {
          reject(err);
        }
      };
      timeoutId = setTimeout(poll, 1000);
    });
  };

  // ── Catalog select → auto-fill form
  const handleCatalogSelect = (entry: CatalogEntry, freeUBlocks: { start: number; size: number }[], slots: number) => {
    const firstFreeBlock = freeUBlocks.find(b => b.size >= (entry.u_height || 1));
    const nextU = firstFreeBlock ? firstFreeBlock.start : 1;

    setFormInitialData({
      name:              `${entry.manufacturer} ${entry.model}`,
      equipment_type:    entry.equipment_type,
      u_height:          entry.u_height,
      u_position:        nextU <= slots ? nextU : 1,
      mounting_position: 'Front',
      is_zero_u:         false,
      weight_kg:         entry.weight_kg || 10,
      power_w:           (entry as any).power_watts || (entry as any).power_w || 0,
      serial_number:     '',
      asset_tag:         '',
      status:            'Active',
      attributes:        '{}',
      catalog_id:        entry.id,
    });
    setEditingItem(null);
    setIsFormOpen(true);
  };

  // ── Catalog search for form
  const handleCatalogSearchForForm = async (query: string): Promise<CatalogEntry[]> => {
    const res = await api.get('/api/equipment/catalog', { params: { query } });
    return res.data;
  };

  return {
    isSaving,
    handleEquipmentMove,
    handleFormSubmit,
    handleAISuggest,
    handleCatalogSelect,
    handleCatalogSearchForForm,
  };
}
